import { faPlus as fasPlus } from "@awesome.me/kit-3777c2145e/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import autobind from "autobind-decorator";
import React from "react";
import type { ReactNode } from "react";
import { ListGroup, ListGroupItem } from "react-bootstrap";
import type { FieldRenderProps } from "react-final-form";
import { Button } from "../../Button";
import { FormItem } from "../../FormItem";
import type { FormItemProps, FormItemState } from "../../FormItem";
import { FormLoanSimpleItem } from "./FormLoanSimpleItem";
import type { FormLoanSimpleData } from "./FormLoanSimpleItem.data";
import { FormLoanSimpleModal } from "./FormLoanSimpleModal";

export interface FormLoanSimpleProps extends FormItemProps<FormLoanSimpleData[]> {
	size?: "sm";
	onChange?(existingLoanList: FormLoanSimpleData[]): void;
}

interface State extends FormItemState {
	showModal: boolean;
	editKey?: number;
}

export class FormLoanSimple extends FormItem<FormLoanSimpleData[], FormLoanSimpleProps, State> {
	protected getInitialState(): State {
		return {
			...super.getInitialState(),
			showModal: false,
			editKey: undefined,
		};
	}

	protected renderField(fieldProps: FieldRenderProps<FormLoanSimpleData[]>): ReactNode {
		const { disabled, tabIndex, readOnly, size } = this.props;
		const {
			input: { value: existingLoanList = [], name },
		} = fieldProps;
		const { editKey, showModal } = this.state;

		const initialValues: FormLoanSimpleData = existingLoanList[editKey] || {
			debt: undefined,
			loanType: undefined,
			loanTypeName: undefined,
		};

		return (
			<>
				<ListGroup className="mt-3">
					{existingLoanList.length === 0 && this.renderEmpty(fieldProps)}
					{existingLoanList.map((loan, key) => (
						<FormLoanSimpleItem
							key={key}
							disabled={disabled || readOnly}
							size={size}
							onEdit={() => this.handleEditItem(fieldProps, key)}
							onDelete={() => this.handleDeleteItem(existingLoanList, fieldProps, key)}
							{...loan}
						/>
					))}
				</ListGroup>
				<div className="text-center">
					<Button
						data-name={name}
						className="mt-3"
						variant="success"
						icon={<FontAwesomeIcon icon={fasPlus} />}
						tabIndex={tabIndex}
						disabled={this.hasNotLoan(fieldProps.input.value) || disabled || readOnly}
						onFocus={(event: React.FocusEvent<HTMLInputElement>) => {
							this.handleFocus(event, fieldProps);
						}}
						onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
							this.handleBlur(event, fieldProps);
						}}
						onClick={this.handleAddLoanClick}
					>
						Meglévő hitel hozzáadása
					</Button>
				</div>
				<FormLoanSimpleModal
					show={showModal}
					initialValues={initialValues}
					onSave={(loan: FormLoanSimpleData) =>
						this.handleModalSave(existingLoanList, fieldProps, loan, editKey)
					}
					onHide={this.handleModalHide}
				/>
			</>
		);
	}

	private renderEmpty(fieldProps: FieldRenderProps<FormLoanSimpleData[]>): ReactNode {
		const { disabled, readOnly } = this.props;

		return (
			<ListGroupItem variant="warning">
				<p className="text-center">
					A meglévő hitelek, hitelkeretek, hitelkártyák <strong>jelentősen befolyásolják</strong> a felvehető
					hitel összegét, és a banki elbírálás eredményét!
				</p>
				<div className="d-flex mb-2">
					<div className="mx-auto custom-control custom-switch">
						<input
							disabled={disabled || readOnly}
							type="checkbox"
							id={`${this.id}_toggle`}
							onChange={(event) => {
								this.handleChange(event.currentTarget.checked ? [] : undefined, fieldProps);
							}}
							checked={this.hasNotLoan(fieldProps.input.value)}
							className="custom-control-input"
						/>
						<label className="custom-control-label" htmlFor={`${this.id}_toggle`}>
							Nincs meglévő hitelem!
						</label>
					</div>
				</div>
			</ListGroupItem>
		);
	}

	private hasNotLoan(value: FormLoanSimpleData[]): boolean {
		return Array.isArray(value) && value.length === 0;
	}

	@autobind
	private handleEditItem(_fieldProps: FieldRenderProps<FormLoanSimpleData[]>, key: number): void {
		this.setState({
			showModal: true,
			editKey: key,
		});
	}

	@autobind
	private handleDeleteItem(
		existingLoanList: FormLoanSimpleData[],
		fieldProps: FieldRenderProps<FormLoanSimpleData[]>,
		key: number,
	): void {
		const newExistingLoanList = [...existingLoanList];
		newExistingLoanList.splice(key, 1);

		this.handleChange(newExistingLoanList.length > 0 ? newExistingLoanList : undefined, fieldProps);
	}

	@autobind
	private handleAddLoanClick(): void {
		this.setState({
			showModal: true,
		});
	}

	@autobind
	private handleModalHide(): void {
		this.setState({
			showModal: false,
			editKey: undefined,
		});
	}

	@autobind
	private handleModalSave(
		existingLoanList: FormLoanSimpleData[],
		fieldProps: FieldRenderProps<FormLoanSimpleData[]>,
		loan: FormLoanSimpleData,
		key?: number,
	): void {
		const newExistingLoanList = [...existingLoanList];

		if (key !== undefined) {
			newExistingLoanList[key] = loan;
		} else {
			newExistingLoanList.push(loan);
		}
		this.setState({
			showModal: false,
			editKey: undefined,
		});

		this.handleChange(newExistingLoanList, fieldProps);
	}

	@autobind
	protected handleValidate(value: FormLoanSimpleData[], allValues: any): string {
		if (value === undefined) {
			return "Jelöld be, hogy nem rendelkezel hitellel, vagy adj hozzá legalább egy hitelt!";
		}

		return super.handleValidate(value, allValues);
	}

	protected isValidationShow(fieldProps: FieldRenderProps<FormLoanSimpleData[]>): boolean {
		const { showModal } = this.state;

		if (showModal) {
			return false;
		}

		return super.isValidationShow(fieldProps);
	}
}
